<script setup lang="ts">
// Page admin V2 « Exports Cegid (TRA) » — génère les fichiers d'écritures
// comptables (factures Odoo 18) et liste les fichiers déjà générés.
// Accès : SUPER_ADMIN (guard router `requiresSuperAdmin` + endpoints back).
// Design : cf src/v2/design-system/pages/exports-cegid.md (+ MASTER.md).
import { computed, onMounted, ref } from 'vue'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline'
import {
  useCegidExportsAdminStore,
  type CegidExportBatchDetail,
  type CegidExportBatchRow,
  type CegidExportBatchStatus,
  type CegidExportBatchType,
  type CegidExportTrigger,
  type GenerateExportResult,
} from '@/v2/stores/useCegidExportsAdminStore'
import HelpButton from '@/components/common/help/HelpButton.vue'

const store = useCegidExportsAdminStore()

// ── Formulaire de génération ─────────────────────────────────────────────────
function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

// Défaut aligné sur le cron : du J-7 à la veille (les factures du jour peuvent
// encore bouger dans Odoo).
const dateFrom = ref(isoDaysAgo(7))
const dateTo = ref(isoDaysAgo(1))
const forceReexport = ref(false)
const sendEmail = ref(false)
const generateError = ref('')
const result = ref<GenerateExportResult | null>(null)

const rangeInvalid = computed(() => !dateFrom.value || !dateTo.value || dateFrom.value > dateTo.value)

async function generate() {
  if (rangeInvalid.value || store.isGenerating) return
  generateError.value = ''
  result.value = null
  const res = await store.generate(dateFrom.value, dateTo.value, forceReexport.value, sendEmail.value)
  if (res.ok && res.data) {
    result.value = res.data
    await store.fetchBatches()
  } else {
    generateError.value = res.message ?? 'Échec de la génération'
  }
}

// Un batch peut exister sans fichier (toutes les lignes mises de côté en erreur) :
// on ne propose le téléchargement que si un fichier a réellement été produit.
const resultHasFiles = computed(() => !!result.value && (!!result.value.invoices.filename || !!result.value.tiers.filename))

// ── Historique ───────────────────────────────────────────────────────────────
onMounted(() => {
  store.fetchBatches()
})

// ── Modal détail ─────────────────────────────────────────────────────────────
const detailDialog = ref<HTMLDialogElement | null>(null)
const detail = ref<CegidExportBatchDetail | null>(null)
const isLoadingDetail = ref(false)

async function openDetail(row: CegidExportBatchRow) {
  detail.value = null
  isLoadingDetail.value = true
  detailDialog.value?.showModal()
  detail.value = await store.fetchDetail(row.id)
  isLoadingDetail.value = false
}

// ── Téléchargement ───────────────────────────────────────────────────────────
const downloadingId = ref<string | null>(null)

async function download(batchId: string, filename?: string | null) {
  downloadingId.value = batchId
  try {
    await store.downloadFile(batchId, filename)
  } finally {
    downloadingId.value = null
  }
}

// ── Helpers d'affichage ──────────────────────────────────────────────────────
const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' })

function formatDateTime(iso: string | null): string {
  return iso ? dateTimeFmt.format(new Date(iso)) : '—'
}

/** YYYY-MM-DD → DD/MM/YYYY (sans passer par Date : pas de surprise de fuseau). */
function formatDay(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function formatPeriod(row: { dateFrom: string | null; dateTo: string | null }): string {
  if (!row.dateFrom || !row.dateTo) return '—'
  if (row.dateFrom === row.dateTo) return formatDay(row.dateFrom)
  return `${formatDay(row.dateFrom)} → ${formatDay(row.dateTo)}`
}

const TYPE_LABELS: Record<CegidExportBatchType, string> = {
  INVOICES: 'Factures',
  TIERS: 'Tiers',
  RIBS: 'RIB',
}

const TRIGGER_LABELS: Record<CegidExportTrigger, string> = {
  CRON: 'Cron quotidien',
  MANUAL: 'Manuel',
  API: 'API',
}

const STATUS_BADGES: Record<CegidExportBatchStatus, { label: string; cls: string }> = {
  PENDING: { label: 'En attente', cls: 'badge-ghost' },
  GENERATING: { label: 'Génération', cls: 'badge-info' },
  READY: { label: 'Généré', cls: 'badge-success' },
  UPLOADING: { label: 'Envoi Cegid', cls: 'badge-info' },
  UPLOADED: { label: 'Envoyé Cegid', cls: 'badge-info' },
  PROCESSING: { label: 'Traitement Cegid', cls: 'badge-info' },
  COMPLETED: { label: 'Traité Cegid', cls: 'badge-success' },
  PARTIAL: { label: 'Partiel', cls: 'badge-warning' },
  FAILED: { label: 'Échec', cls: 'badge-error' },
}

const LINE_STATUS_BADGES: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'En attente', cls: 'badge-ghost' },
  OK: { label: 'OK', cls: 'badge-success' },
  ERROR: { label: 'Erreur', cls: 'badge-error' },
  SKIPPED: { label: 'Ignorée', cls: 'badge-warning' },
}
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header>
      <div class="flex items-center gap-1">
        <h1 class="text-2xl font-bold">Exports Cegid (TRA)</h1>
        <HelpButton />
      </div>
      <p class="mt-1 max-w-prose text-base-content/80">
        Génère les fichiers d'écritures comptables des factures Odoo 18 à importer dans Cegid, et retrouve les fichiers
        déjà générés (à la demande ou par le cron quotidien).
      </p>
    </header>

    <!-- ── Générer un export ─────────────────────────────────────────────── -->
    <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="generate-title">
      <div class="card-body p-4 md:p-6">
        <h2 id="generate-title" class="card-title text-lg font-semibold">Générer un export</h2>
        <p class="max-w-prose text-base-content/80">
          Factures comptabilisées (ventes, achats et avoirs) dont la date de facture est dans la plage. Source :
          <span class="font-medium">Odoo 18</span>. Les factures déjà présentes dans le grand livre Cegid sont exclues
          automatiquement (anti-doublon).
        </p>

        <form class="mt-3 flex flex-wrap items-end gap-4" @submit.prevent="generate">
          <div>
            <label class="label" for="export-date-from"><span class="label-text">Du</span></label>
            <input
              id="export-date-from"
              v-model="dateFrom"
              type="date"
              class="input input-bordered tabular-nums"
              :class="{ 'input-error': rangeInvalid && dateFrom && dateTo }"
              required
            />
          </div>
          <div>
            <label class="label" for="export-date-to"><span class="label-text">Au (inclus)</span></label>
            <input
              id="export-date-to"
              v-model="dateTo"
              type="date"
              class="input input-bordered tabular-nums"
              :class="{ 'input-error': rangeInvalid && dateFrom && dateTo }"
              required
            />
          </div>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="rangeInvalid || store.isGenerating"
            :aria-busy="store.isGenerating"
          >
            <span v-if="store.isGenerating" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
            <DocumentPlusIcon v-else class="h-5 w-5" aria-hidden="true" />
            {{ store.isGenerating ? 'Génération en cours…' : 'Générer le fichier TRA' }}
          </button>
        </form>
        <p v-if="rangeInvalid && dateFrom && dateTo" class="mt-1 text-sm text-error">
          La date de début doit être antérieure ou égale à la date de fin.
        </p>

        <label class="mt-2 flex w-fit cursor-pointer items-center gap-2">
          <input v-model="forceReexport" type="checkbox" class="checkbox checkbox-sm" />
          <span class="label-text">Ré-exporter les factures déjà incluses dans un export précédent</span>
        </label>

        <label class="mt-1 flex w-fit cursor-pointer items-center gap-2">
          <input v-model="sendEmail" type="checkbox" class="checkbox checkbox-sm" />
          <span class="label-text">Envoyer le fichier par email aux destinataires configurés</span>
        </label>

        <div v-if="generateError" role="alert" class="alert alert-error mt-4">
          <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{{ generateError }}</span>
        </div>

        <!-- Résultat de la génération -->
        <div v-if="result" role="status" class="mt-4 rounded-box border border-base-300 bg-base-200 p-4">
          <h3 class="flex items-center gap-2 font-semibold">
            <CheckCircleIcon v-if="resultHasFiles" class="h-5 w-5 text-success" aria-hidden="true" />
            <InformationCircleIcon v-else class="h-5 w-5 text-info" aria-hidden="true" />
            Résultat — du {{ formatDay(result.period.from) }} au {{ formatDay(result.period.to) }}
          </h3>

          <div
            v-if="result.email"
            class="mt-2 flex items-center gap-2 text-sm"
            :class="result.email.sent ? 'text-success' : 'text-error'"
          >
            <CheckCircleIcon v-if="result.email.sent" class="h-4 w-4 shrink-0" aria-hidden="true" />
            <ExclamationTriangleIcon v-else class="h-4 w-4 shrink-0" aria-hidden="true" />
            <span v-if="result.email.sent">Email envoyé à {{ result.email.recipients.join(', ') }}.</span>
            <span v-else>
              Envoi email échoué{{
                result.email.recipients.length === 0
                  ? ' — aucun destinataire configuré (CEGID_EXPORT_DAILY_EMAILS)'
                  : result.email.error
                    ? ` : ${result.email.error}`
                    : ''
              }}.
            </span>
          </div>

          <p class="mt-2 text-base-content/80">
            {{ result.invoicesListedInOdoo }} facture{{ result.invoicesListedInOdoo > 1 ? 's' : '' }} trouvée{{
              result.invoicesListedInOdoo > 1 ? 's' : ''
            }}
            dans Odoo sur la période.
          </p>

          <p v-if="result.invoicesListedInOdoo === 0" class="mt-1 text-base-content/80">
            Aucun fichier n'a été généré.
          </p>
          <p v-else-if="!result.invoices.filename" class="mt-1 text-base-content/80">
            Toutes les factures ont été exclues ou mises de côté (voir détail ci-dessous) — aucun fichier factures
            généré.
          </p>

          <ul v-if="resultHasFiles" role="list" class="mt-3 space-y-2">
            <li
              v-if="result.tiers.filename"
              class="flex flex-wrap items-center gap-3 rounded-field border border-base-300 bg-base-100 p-3"
            >
              <span class="badge badge-sm badge-neutral">Tiers</span>
              <span class="font-medium">{{ result.tiers.filename }}</span>
              <span class="text-base-content/80"
                >{{ result.tiers.newTiersDetected }} nouveau{{ result.tiers.newTiersDetected > 1 ? 'x' : '' }} tiers</span
              >
              <button
                type="button"
                class="btn btn-outline btn-sm"
                :disabled="downloadingId === result.tiers.batchId"
                @click="download(result.tiers.batchId!, result.tiers.filename)"
              >
                <ArrowDownTrayIcon class="h-4 w-4" aria-hidden="true" />
                Télécharger
              </button>
            </li>
            <li
              v-if="result.invoices.filename"
              class="flex flex-wrap items-center gap-3 rounded-field border border-base-300 bg-base-100 p-3"
            >
              <span class="badge badge-sm badge-neutral">Factures</span>
              <span class="font-medium">{{ result.invoices.filename }}</span>
              <span class="text-base-content/80">
                {{ result.invoices.exported }} facture{{ result.invoices.exported > 1 ? 's' : '' }} —
                {{ result.invoices.lineCount }} ligne{{ result.invoices.lineCount > 1 ? 's' : '' }} d'écriture
              </span>
              <button
                type="button"
                class="btn btn-outline btn-sm"
                :disabled="downloadingId === result.invoices.batchId"
                @click="download(result.invoices.batchId!, result.invoices.filename)"
              >
                <ArrowDownTrayIcon class="h-4 w-4" aria-hidden="true" />
                Télécharger
              </button>
            </li>
          </ul>

          <div v-if="result.tiers.filename && result.invoices.filename" role="alert" class="alert alert-warning mt-3">
            <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Importer le fichier <strong>TIERS</strong> dans Cegid avant le fichier <strong>FACTURES</strong>.</span>
          </div>

          <details v-if="result.invoices.excluded.length" class="collapse collapse-arrow mt-3 border border-base-300 bg-base-100">
            <summary class="collapse-title font-medium">
              Factures exclues ({{ result.invoices.excluded.length }})
            </summary>
            <div class="collapse-content">
              <ul role="list" class="space-y-1">
                <li v-for="e in result.invoices.excluded" :key="e.invoiceName" class="text-base-content/80">
                  <span class="font-medium text-base-content">{{ e.invoiceName }}</span> — {{ e.reason }}
                </li>
              </ul>
            </div>
          </details>

          <details v-if="result.invoices.warnings.length" class="collapse collapse-arrow mt-2 border border-base-300 bg-base-100">
            <summary class="collapse-title font-medium">
              Avertissements ({{ result.invoices.warnings.length }})
            </summary>
            <div class="collapse-content">
              <ul role="list" class="space-y-1">
                <li v-for="w in result.invoices.warnings" :key="w.invoiceName" class="text-base-content/80">
                  <span class="font-medium text-base-content">{{ w.invoiceName }}</span> — {{ w.messages.join(' · ') }}
                </li>
              </ul>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- ── Fichiers générés ──────────────────────────────────────────────── -->
    <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="history-title">
      <div class="card-body p-4 md:p-6">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 id="history-title" class="card-title text-lg font-semibold">
            Fichiers générés
            <span v-if="store.batches.length" class="badge badge-sm badge-ghost tabular-nums">{{ store.batches.length }}</span>
          </h2>
          <button
            type="button"
            class="btn btn-ghost"
            :disabled="store.isLoadingBatches"
            :aria-busy="store.isLoadingBatches"
            @click="store.fetchBatches()"
          >
            <ArrowPathIcon class="h-5 w-5" :class="{ 'animate-spin motion-reduce:animate-none': store.isLoadingBatches }" aria-hidden="true" />
            Rafraîchir
          </button>
        </div>

        <div v-if="store.isLoadingBatches && !store.batches.length" class="py-8 text-center">
          <span class="loading loading-spinner" aria-hidden="true"></span>
          <p class="mt-2 text-base-content/80">Chargement des exports…</p>
        </div>

        <p v-else-if="!store.batches.length" class="py-8 text-center text-base-content/80">
          Aucun export pour le moment — utilisez le formulaire ci-dessus pour générer un premier fichier TRA.
        </p>

        <template v-else>
          <!-- Tableau (tablette / desktop ≥ md) -->
          <div class="-mx-4 mt-2 hidden overflow-x-auto md:mx-0 md:block">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Généré le</th>
                  <th scope="col">Type</th>
                  <th scope="col">Période couverte</th>
                  <th scope="col">Déclenchement</th>
                  <th scope="col">Statut</th>
                  <th scope="col" class="text-right">Lignes</th>
                  <th scope="col"><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in store.batches" :key="b.id">
                  <td class="whitespace-nowrap tabular-nums">{{ formatDateTime(b.createdAt) }}</td>
                  <td>
                    <span class="badge badge-sm badge-neutral">{{ TYPE_LABELS[b.type] ?? b.type }}</span>
                  </td>
                  <td class="whitespace-nowrap tabular-nums">{{ formatPeriod(b) }}</td>
                  <td>
                    {{ b.trigger ? TRIGGER_LABELS[b.trigger] : '—' }}
                    <span v-if="b.triggeredBy" class="block text-sm text-base-content/70">{{ b.triggeredBy }}</span>
                  </td>
                  <td>
                    <span class="badge badge-sm" :class="STATUS_BADGES[b.status]?.cls">
                      {{ STATUS_BADGES[b.status]?.label ?? b.status }}
                    </span>
                    <span v-if="b.lineStatusCounts.ERROR" class="ml-1 badge badge-sm badge-error tabular-nums">
                      {{ b.lineStatusCounts.ERROR }} err.
                    </span>
                  </td>
                  <td class="text-right tabular-nums">{{ b.lineCount }}</td>
                  <td>
                    <div class="flex justify-end gap-1">
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :disabled="!b.hasFile || downloadingId === b.id"
                        :aria-busy="downloadingId === b.id"
                        :aria-label="`Télécharger ${b.filename ?? 'le fichier'}`"
                        :title="b.hasFile ? `Télécharger ${b.filename}` : 'Aucun fichier généré'"
                        @click="download(b.id, b.filename)"
                      >
                        <ArrowDownTrayIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :aria-label="`Voir le détail de l'export du ${formatDateTime(b.createdAt)}`"
                        title="Voir le détail"
                        @click="openDetail(b)"
                      >
                        <EyeIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Cartes (mobile < md) — tableau dense illisible sur petit écran -->
          <ul role="list" class="mt-2 flex flex-col gap-2 md:hidden">
            <li v-for="b in store.batches" :key="b.id" class="rounded-lg border border-base-200 p-3">
              <div class="flex items-start justify-between gap-2">
                <div class="flex min-w-0 flex-wrap items-center gap-2">
                  <span class="badge badge-sm badge-neutral">{{ TYPE_LABELS[b.type] ?? b.type }}</span>
                  <span class="tabular-nums text-sm text-base-content/70">{{ formatDateTime(b.createdAt) }}</span>
                </div>
                <span class="badge badge-sm shrink-0" :class="STATUS_BADGES[b.status]?.cls">
                  {{ STATUS_BADGES[b.status]?.label ?? b.status }}
                </span>
              </div>
              <dl class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                <div class="col-span-2 flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Période couverte</dt>
                  <dd class="tabular-nums text-base-content">{{ formatPeriod(b) }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Déclenchement</dt>
                  <dd class="text-base-content">
                    {{ b.trigger ? TRIGGER_LABELS[b.trigger] : '—' }}
                    <span v-if="b.triggeredBy" class="block text-xs text-base-content/70">{{ b.triggeredBy }}</span>
                  </dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Lignes</dt>
                  <dd class="text-base-content">
                    <span class="tabular-nums">{{ b.lineCount }}</span>
                    <span v-if="b.lineStatusCounts.ERROR" class="ml-1 badge badge-sm badge-error tabular-nums">
                      {{ b.lineStatusCounts.ERROR }} err.
                    </span>
                  </dd>
                </div>
              </dl>
              <div class="mt-2 flex items-center gap-1 border-t border-base-200 pt-2">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm gap-1"
                  :disabled="!b.hasFile || downloadingId === b.id"
                  :aria-busy="downloadingId === b.id"
                  :title="b.hasFile ? `Télécharger ${b.filename}` : 'Aucun fichier généré'"
                  @click="download(b.id, b.filename)"
                >
                  <ArrowDownTrayIcon class="h-4 w-4" aria-hidden="true" />
                  Télécharger
                </button>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm gap-1"
                  @click="openDetail(b)"
                >
                  <EyeIcon class="h-4 w-4" aria-hidden="true" />
                  Détail
                </button>
              </div>
            </li>
          </ul>
        </template>
      </div>
    </section>

    <!-- ── Modal détail ──────────────────────────────────────────────────── -->
    <dialog ref="detailDialog" class="modal">
      <div class="modal-box max-w-3xl">
        <h3 class="text-lg font-semibold">Détail de l'export</h3>

        <div v-if="isLoadingDetail" class="py-8 text-center">
          <span class="loading loading-spinner" aria-hidden="true"></span>
          <p class="mt-2 text-base-content/80">Chargement…</p>
        </div>

        <template v-else-if="detail">
          <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            <div>
              <dt class="text-sm text-base-content/70">Fichier</dt>
              <dd class="font-medium break-all">{{ detail.filename ?? '—' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Période couverte</dt>
              <dd class="tabular-nums">{{ formatPeriod(detail) }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Type</dt>
              <dd>{{ TYPE_LABELS[detail.type] ?? detail.type }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Source</dt>
              <dd>{{ detail.sourceCode ?? '—' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Déclenchement</dt>
              <dd>
                {{ detail.trigger ? TRIGGER_LABELS[detail.trigger] : '—' }}
                <span v-if="detail.triggeredBy" class="text-base-content/70">· {{ detail.triggeredBy }}</span>
              </dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Statut</dt>
              <dd>
                <span class="badge badge-sm" :class="STATUS_BADGES[detail.status]?.cls">
                  {{ STATUS_BADGES[detail.status]?.label ?? detail.status }}
                </span>
              </dd>
            </div>
          </dl>

          <div class="mt-4 max-h-96 overflow-y-auto overflow-x-auto">
            <table class="table table-sm">
              <thead class="sticky top-0 bg-base-100">
                <tr>
                  <th scope="col" class="text-right">#</th>
                  <th scope="col">Document</th>
                  <th scope="col">Société</th>
                  <th scope="col">Date</th>
                  <th scope="col">Statut</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in detail.lines" :key="l.id">
                  <td class="text-right tabular-nums">{{ l.lineNumber }}</td>
                  <td class="font-medium">{{ l.label ?? l.entityUuid }}</td>
                  <td>{{ l.companyName ?? '—' }}</td>
                  <td class="whitespace-nowrap tabular-nums">{{ formatDay(l.invoiceDate) }}</td>
                  <td>
                    <span class="badge badge-sm" :class="LINE_STATUS_BADGES[l.status]?.cls">
                      {{ LINE_STATUS_BADGES[l.status]?.label ?? l.status }}
                    </span>
                    <p v-if="l.errorMessage" class="mt-0.5 text-sm text-error">{{ l.errorMessage }}</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="modal-action">
            <button
              v-if="detail.hasFile"
              type="button"
              class="btn btn-primary"
              :disabled="downloadingId === detail.id"
              :aria-busy="downloadingId === detail.id"
              @click="download(detail.id, detail.filename)"
            >
              <ArrowDownTrayIcon class="h-5 w-5" aria-hidden="true" />
              Télécharger
            </button>
            <form method="dialog">
              <button class="btn btn-ghost">Fermer</button>
            </form>
          </div>
        </template>

        <p v-else class="py-8 text-center text-base-content/80">Impossible de charger le détail de cet export.</p>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>Fermer</button>
      </form>
    </dialog>
  </div>
</template>
